import React, { FC, ReactNode, useMemo, useRef } from 'react';
import { ReportFullscreenWrapper } from './components/wrappers/ReportFullscreenWrapper';
import { useReportStyles } from './report.styles';
import { useNativeFullscreen } from './hooks/use-fullscreen-support.hook';
import { AspectRatio, getScalingUnit, ScalingStrategy } from './report.model';
import { ReportContext } from './report.context';
import { ReportPage } from './components/ReportPage';
import { ReportDefaultWrapper } from './components/wrappers/ReportDefaultWrapper';
import { ReportPrintWrapper } from './components/wrappers/ReportPrintWrapper';
import {
  Box,
  CircularProgress,
  IconButton as MuiIconButton,
} from '@mui/material';
import amplitudeLog from '@client/amplitude';
import { AMP_REPORT_ENTER_FULLSCREEN_MODE } from '@shared/constants';
import { ReportText } from './components/ReportText';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getScalingSizeFn } from './report-style.helpers';
import { ReportTable } from './components/ReportTable';
import { IconButton } from '@ui/buttons';
import { Tooltip } from '@ui/Tooltip';
import {
  getReportPanelPageItems,
  getVisibleReportPages,
  toReportPageItems,
} from './report-visibility.helpers';
import type { ReportPageVisibility } from './report.model';

type ToolbarProps = {
  onToggleFullscreen: () => void;
  onExportPdf?: () => void;
  isExporting?: boolean;
  className: string;
  slideCountClassName: string;
  /** Pages that reach the canvas — skipped slides are not counted. */
  slideCount: number;
};

const Toolbar: FC<ToolbarProps> = ({
  onToggleFullscreen,
  onExportPdf,
  isExporting,
  className,
  slideCountClassName,
  slideCount,
}) => {
  return (
    <div className={className}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span className={slideCountClassName}>
          {`${slideCount} ${slideCount === 1 ? 'slide' : 'slides'}`}
        </span>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {onExportPdf &&
            (isExporting ? (
              <Tooltip title="Generating PDF..." showWhenDisabled>
                <MuiIconButton size="small" disabled>
                  <CircularProgress size={18} color="inherit" />
                </MuiIconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Download as PDF">
                <IconButton
                  category="ui"
                  icon="download"
                  onClick={onExportPdf}
                />
              </Tooltip>
            ))}
          <Tooltip title="Fullscreen view">
            <IconButton
              category="mui"
              icon="Fullscreen"
              onClick={() => onToggleFullscreen()}
            />
          </Tooltip>
        </Box>
      </Box>
    </div>
  );
};

type ReportProps = {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode | ReactNode[];
  aspectRatio?: AspectRatio;
  scalingStrategy?: ScalingStrategy;
  hideToolbar?: boolean;
  onExportPdf?: () => void;
  isExporting?: boolean;
  /** Lets the thumbnail panel drop pages from the canvas, fullscreen and PDF. */
  pageVisibility?: ReportPageVisibility;
  /**
   * Stable key for thumbnail PNG cache. When unchanged across Report remounts,
   * the sidebar reuses captured images instead of recapturing.
   */
  thumbnailContentKey?: string;
};

const ReportRoot: FC<ReportProps> = ({
  header,
  footer,
  children,
  aspectRatio = '16/9',
  scalingStrategy = 'container',
  hideToolbar = false,
  onExportPdf,
  isExporting,
  pageVisibility,
  thumbnailContentKey = '',
}) => {
  const fullscreenRef = useRef<HTMLDivElement>(null);

  const nativeFullscreenUtils = useNativeFullscreen();

  // The previous resize-aware switch from `container` to `window` units during
  // drag triggered a React re-render + visible layout snap each time the user
  // let go of the window edge. Container queries are browser-native and cheap;
  // the perceived lag was the strategy switch itself, so always honour the
  // caller's strategy and let CSS recompute cqw values directly.
  const getScalingSize = getScalingSizeFn(getScalingUnit(scalingStrategy));
  const { classes } = useReportStyles({ getScalingSize });

  const pageItems = useMemo(() => {
    const childrenArray = guards.is.array(children) ? children : [children];
    const items = toReportPageItems(
      childrenArray.filter(guards.is.reactElement),
      pageVisibility?.hiddenPageKeys,
    );
    return getReportPanelPageItems(items, !!pageVisibility?.onTogglePageHidden);
  }, [
    children,
    pageVisibility?.hiddenPageKeys,
    pageVisibility?.onTogglePageHidden,
  ]);

  // Hidden pages only render as (dimmed) thumbnails when the viewer can unskip them.
  const visiblePages = useMemo(
    () => getVisibleReportPages(pageItems),
    [pageItems],
  );

  const onToggleFullscreen = () => {
    const { isSupported, getActiveElement, request, exit } =
      nativeFullscreenUtils;
    const element = fullscreenRef.current;
    if (!isSupported || !element) return;
    if (getActiveElement() !== element) {
      request(element);
      amplitudeLog(AMP_REPORT_ENTER_FULLSCREEN_MODE);
    } else {
      exit();
    }
  };

  const toolbar = (
    <Toolbar
      className={classes.reportToolbar}
      slideCountClassName={classes.reportToolbarSlideCount}
      slideCount={visiblePages.length}
      onToggleFullscreen={onToggleFullscreen}
      onExportPdf={onExportPdf}
      isExporting={isExporting}
    />
  );

  return (
    <ReportContext.Provider
      value={{
        header,
        footer,
        aspectRatio,
        scalingStrategy,
        isFullscreen: nativeFullscreenUtils.isActive,
        isPrint: hideToolbar,
        exitFullscreen: nativeFullscreenUtils.exit,
      }}
    >
      <div className={classes.report} ref={fullscreenRef}>
        {nativeFullscreenUtils.isActive ? (
          <ReportFullscreenWrapper>{visiblePages}</ReportFullscreenWrapper>
        ) : hideToolbar ? (
          <ReportPrintWrapper>{visiblePages}</ReportPrintWrapper>
        ) : (
          <ReportDefaultWrapper
            toolbar={toolbar}
            pageItems={pageItems}
            onTogglePageHidden={pageVisibility?.onTogglePageHidden}
            thumbnailContentKey={thumbnailContentKey}
          >
            {visiblePages}
          </ReportDefaultWrapper>
        )}
      </div>
    </ReportContext.Provider>
  );
};

export const Report = Object.assign(ReportRoot, {
  Page: ReportPage,
  Text: ReportText,
  Table: ReportTable,
});
